import React, { useState } from 'react';

import { createListCardArray } from '../../../helpers/list-page/list-page-helpers';
import {
  cardIsEditorial,
  cardIsEvent,
  cardIsOffer,
  pageIsEditorial,
} from '../../../helpers/list-page/type-guards';
import { useGetLibraryDictionary } from '../../../hooks/use-get-library-dictionary';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { ILibraryOpeningHours } from '../../../queries/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '../../../queries/fragments/libraryOpeningHoursException';
import { IMeetingPlacePageEditorial } from '../../../queries/fragments/meetingPlacePageEditorial';
import { IMeetingPlacePageLegalDocument } from '../../../queries/fragments/meetingPlacePageLegalDocument';
import { BlockWrapper } from '../../block-wrapper/block-wrapper';
import { RenderBlocks } from '../../blocks/render-blocks';
import { CardEditorialItem } from '../../cards/card-editorial-item';
import { CardEvent } from '../../cards/card-event';
import { CardOffer } from '../../cards/card-offer';
import { PageComponentImage } from '../image/page-component-image';
import ShowMore from '../list-page/show-more';
import { PageComponentMain } from '../main/page-component-main';

interface EditorialPageProps {
  page: IMeetingPlacePageEditorial | IMeetingPlacePageLegalDocument;
  integrationId?: string;
  node_locale: string;
  showSocialShare: boolean;
  meetingPlaceOpeningHours?: {
    openingHours?: ILibraryOpeningHours;
    openingHoursExceptions?: ILibraryOpeningHoursException[];
    timezone?: string;
  };
  tenantOpeningHoursDetails?: {
    name: string;
    openingHours: ILibraryOpeningHours;
    openingHoursExceptions: ILibraryOpeningHoursException[];
    timezone: string;
    slug: string;
  }[];
}

export const EditorialPage = ({
  page,
  integrationId,
  node_locale,
  showSocialShare,
  meetingPlaceOpeningHours,
  tenantOpeningHoursDetails,
}: EditorialPageProps) => {
  const isMobile = useIsMobile();
  const imageURL = page?.mediaField?.jsonBynderAsset?.originalUrl;
  const dictionary = useGetLibraryDictionary(page.node_locale);

  const editorialCards =
    (pageIsEditorial(page) &&
      page?.editorialPageCards &&
      createListCardArray(page.editorialPageCards)) ||
    [];

  const [currPage, setCurrPage] = useState(1);
  const _cardsListSize = 4;
  const slicedList = editorialCards?.slice(0, _cardsListSize * currPage);

  return (
    <div className="grid grid-cols-11 gap-6 pt-5 lg:gap-5">
      <div className="col-span-11 lg:col-span-5">
        <PageComponentImage
          imageURL={imageURL}
          stickyImage={true}
          node_locale={node_locale}
        />
      </div>
      <div className="col-span-11 lg:col-span-6">
        <PageComponentMain
          header={page.name}
          blocks={page?.mainBlock}
          richText={page.body}
          node_locale={node_locale}
          showSocialShare={showSocialShare}
          meetingPlaceOpeningHours={meetingPlaceOpeningHours}
          appDownloadPanelReference={
            pageIsEditorial(page) ? page.appDownloadPanel : undefined
          }
          panelclasses={isMobile ? '!bg-background-primary' : ''}
        />
        {page?.blocks && (
          <div className="-mx-6 bg-brand-white pt-16 lg:-mx-0 lg:mb-5 lg:pt-0">
            {page.blocks.map((block) => (
              <BlockWrapper
                key={block.id}
                className="mb-6 rounded-card-mobile px-6 py-11 last:mb-0 lg:p-20 xl:p-24"
              >
                <RenderBlocks
                  blocks={[block]}
                  integrationId={integrationId}
                  node_locale={node_locale}
                  tenantOpeningHoursDetails={tenantOpeningHoursDetails}
                  meetingPlaceOpeningHours={meetingPlaceOpeningHours}
                />
              </BlockWrapper>
            ))}
          </div>
        )}
        {editorialCards && editorialCards.length > 0 && (
          <div className="-mx-6 bg-brand-white px-6 pt-6 lg:m-0 lg:p-0">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {slicedList?.map((card: any, index: number) => {
                if (cardIsEditorial(card)) {
                  return (
                    <CardEditorialItem
                      card={card}
                      key={card.id}
                      cardIndex={index}
                    />
                  );
                } else if (cardIsOffer(card)) {
                  return (
                    <CardOffer card={card} key={card.id} cardIndex={index} />
                  );
                } else if (cardIsEvent(card)) {
                  return (
                    <CardEvent card={card} key={card.id} cardIndex={index} />
                  );
                }
              })}
            </div>
            {editorialCards && editorialCards.length > _cardsListSize && (
              <ShowMore
                currentPage={currPage}
                maxNum={editorialCards.length}
                setPage={setCurrPage}
                pageSize={_cardsListSize}
                ofString={dictionary?.of}
                showMoreString={dictionary?.showMore}
                showingString={dictionary?.showing}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
